import {Injector} from "@angular/core";
import {SortEvent} from "primeng/api";
import {Observable, skipWhile, Subject} from "rxjs";

export function ensureGlobalSpinnerRemoved() {
  document.getElementById('spinner')?.remove();
}

export function addAngularComponentCssClass() {
  const components = Array.prototype.filter.call(
    document.querySelectorAll('*'),
    e => {
      const tag = e.tagName.toLowerCase();
      return tag.startsWith('app-') || tag.startsWith('mes-') || tag.startsWith('ngx-');
    }
  );
  components.forEach(cmp => cmp.classList.add('__angular-component'))
}

export function openFullscreen() {
  const toggle: boolean = (localStorage.getItem('toggle-full-screen') || 'true') === 'true';
  if (toggle) {
    try {
      let document: any = window.document;
      let elem: any = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(reason => console.warn(reason));
      } else if (elem.mozRequestFullScreen) { /* Firefox */
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) { /* Chrome, Safari & Opera */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) { /* IE/Edge */
        elem.msRequestFullscreen();
      }
    } catch (e) {
      console.warn(e);
    }
  }
}

export function closeFullscreen() {
  try {
    let document: any = window.document;
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  } catch (e) {
    console.warn(e);
  }
}

export function isUndefined(value: any) {
  return typeof value === 'undefined';
}

export function isNull(value: any) {
  return value === null;
}

export function isFunction(value: any) {
  return typeof value === 'function';
}

export function isNumber(value: any) {
  return typeof value === 'number';
}

export function isString(value: any) {
  return typeof value === 'string';
}

export function isBoolean(value: any) {
  return typeof value === 'boolean';
}

export function isObject(value: any) {
  return value !== null && typeof value === 'object';
}

export function isNumberFinite(value: any) {
  return isNumber(value) && isFinite(value);
}

export function isVowel(letter: string): boolean {
  const vowels = ['a', 'e', 'i', 'o', 'u'];

  return vowels.indexOf(letter) !== -1;
}

export function ucFirst(text: string) {
  const [part, ...split] = text.split(/\s/g);

  const ucd = part
    .toLowerCase()
    .split(/(?=['|-])/g)
    .map(
      (word: any) =>
        word.indexOf('-') + word.indexOf("'") > -2
          ? word.slice(0, 2).toUpperCase() + word.slice(2)
          : word.slice(0, 1).toUpperCase() + word.slice(1)
    )
    .join('');

  return [ucd, ...split].join(' ');
}

export function applyPrecision(num: number, precision: number) {
  if (precision <= 0) {
    return Math.round(num);
  }

  const tho = 10 ** precision;

  return Math.round(num * tho) / tho;
}

export function extractDeepPropertyByMapKey(obj: any, map: string): any {
  const keys = map.split('.');
  const head = keys.shift();

  return keys.reduce((prop: any, key: string) => {
    return !isUndefined(prop) && !isNull(prop) && !isUndefined(prop[key]) ? prop[key] : undefined;
  }, obj[head || '']);
}

export function extractDeepPropertyByParentMapKey(obj: any, map: string): any {
  const keys = map.split('.');
  const tail = keys.pop();
  const props = extractDeepPropertyByMapKey(obj, keys.join('.'));

  return {props, tail};
}

export function getKeysTwoObjects(obj: any, other: any): any {
  return [...Object.keys(obj), ...Object.keys(other)].filter((key, index, array) => array.indexOf(key) === index);
}

export function isDeepEqual(obj: any, other: any): any {
  if (!isObject(obj) || !isObject(other)) {
    return obj === other;
  }

  return getKeysTwoObjects(obj, other).every(
    (key: any): boolean => {
      if (!isObject(obj[key]) && !isObject(other[key])) {
        return obj[key] === other[key];
      }
      if (!isObject(obj[key]) || !isObject(other[key])) {
        return false;
      }

      return isDeepEqual(obj[key], other[key]);
    }
  );
}

export function selectElementContents(el) {
  let document: any = window.document;
  let body = document.body, range, sel;
  if (document.createRange && window.getSelection) {
    range = document.createRange();
    sel = window.getSelection();
    sel.removeAllRanges();
    try {
      range.selectNodeContents(el);
      sel.addRange(range);
    } catch (e) {
      range.selectNode(el);
      sel.addRange(range);
    }
  } else if (body.createTextRange) {
    range = body.createTextRange();
    range.moveToElementText(el);
    range.select();
  }
  document.execCommand("Copy");

  setTimeout(() => {
    window.getSelection().removeAllRanges();
  }, 100);
}

export let AppInjector: Injector;

export function setAppInjector(injector: Injector) {
  if (!AppInjector) {
    AppInjector = injector;
  }
}

export function simplifiedRegexSearch(term: string, fieldValues: string[]): boolean {
  const conditionGroupsByOr = term.split(/[|;]/).filter(group => group.length).map(function(e) {
    return e.split(/[&]/).filter(group => group.length).map(String);
  })

  return conditionGroupsByOr.some((orGroup) => {
    return Array.isArray(orGroup)
        ? orGroup.every((andGroup) => prepareMatch(andGroup, fieldValues))
        : prepareMatch(String(orGroup), fieldValues);
  });
}

function prepareMatch(termFragment: string, fieldValues: string[]){
  const isInversed = termFragment.startsWith('!');
  termFragment = isInversed ? termFragment.substring(1) : termFragment;
  const conditions = termFragment.split('*').filter(condition => condition.length);
  return isInversed
      ? fieldValues.every(fieldValue => getMatch(fieldValue, conditions, termFragment, isInversed))
      : fieldValues.some(fieldValue => getMatch(fieldValue, conditions, termFragment, isInversed));
}

function getMatch(value: string, conditions: string[], conditionGroupByOr: string, isInversed: boolean): boolean {
  let i = 1;
  const result = value !== null && value !== undefined && conditions.every(condition => {
    value = value.toLocaleUpperCase();
    condition = condition.toLocaleUpperCase();
    let matchFoundAt: number;
    if (i === 1 && conditionGroupByOr.indexOf('*') > 0) {
      matchFoundAt = value.startsWith(condition) ? 0 : -1;
    } else if (i === conditions.length && conditionGroupByOr.includes('*') && !conditionGroupByOr.endsWith('*')) {
      matchFoundAt = value.endsWith(condition) ? value.lastIndexOf(condition) : -1;
    } else {
      matchFoundAt = value.indexOf(condition);
    }
    value = value.substring(matchFoundAt + condition.length);
    i++;
    // console.log(matchFoundAt !== -1)
    return matchFoundAt !== -1;
  });
  // console.log(isInversed ? !result : result)
  return isInversed ? !result : result;
}

export function removeRedundantKeys(entities: string, keysToKeep: string[]): any[] {
  let parsedEntities = JSON.parse(entities);
  parsedEntities.forEach(entity => {
    Object.keys(entity).forEach(key => {
      if (!keysToKeep.includes(key)) {
        delete entity[key];
      }
    });
  });
  return parsedEntities;
}

export function sortPrimeTableColumn(event: SortEvent) {
  event.data.sort((data1, data2) => {
    if (data1.$isNew === true) {
      return 1;
    }
    let value1 = data1[event.field];
    let value2 = data2[event.field];
    let result = null;
    if (value1 == null && value2 != null)
      result = -1;
    else if (value1 != null && value2 == null)
      result = 1;
    else if (value1 == null && value2 == null)
      result = 0;
    else if (typeof value1 === 'string' && typeof value2 === 'string')
      result = value1.localeCompare(value2);
    else
      result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;
    return (event.order * result);
  });
}

export function skipUntilAll<T>() {
  return (source: Observable<Array<Array<T>>>) => source.pipe(skipWhile(value => !value.every(value1 => value1.length > 0)));
}

type MarkFunctionPropertyNames<Component> = {
  [Key in keyof Component]: Component[Key] extends Function | Subject<any> ? never : Key;
}

type ExcludeFunctionPropertyNames<T extends object> = MarkFunctionPropertyNames<T>[keyof T];
type ExcludeFunctions<T extends object> = Pick<T, ExcludeFunctionPropertyNames<T>>;

export type NgChanges<Component extends object, Props = ExcludeFunctions<Component>> = {
  [Key in keyof Props]: {
    previousValue: Props[Key];
    currentValue: Props[Key];
    firstChange: boolean;
    isFirstChange(): boolean;
  }
}

export function areObjectsEqual(obj1: object, obj2: object, excludedProperties: string[]) {
  if (!obj1 != !obj2) return false;

  const keys1 = Object.keys(obj1).filter(
    (key) => !excludedProperties.includes(key)
  );
  const keys2 = Object.keys(obj2).filter(
    (key) => !excludedProperties.includes(key)
  );

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])) {
      return false;
    }
  }

  return true;
}
